'use client'

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { useRouter, useParams } from 'next/navigation'
import { ArrowLeft, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import {
  syncFplEntry,
  useFplCurrentGameweek,
  useFplEntryPicks,
  useFplEntrySearch,
  useFplEntryGwHistory,
  useFplEntrySeasonHistory,
  useFplEntryMvps,
  useFplEntryNationalities,
  useFplEntryLeagues,
  useFplEntryMarketValue,
  FplApiError,
} from '@/lib/api/fpl-routes'
import type { FplLeagueGameweekSelection } from '@/lib/api/fpl-routes'
import type { FplEntrySummary, FplPick, FplEntryNationality, FplMvpStat, FplEntryMvp } from '@/lib/types/fpl'
import { FplPlayerCard } from '@/components/custom/fpl/fpl-player-card'
import { Spinner } from '@/components/ui/spinner'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { pinScrollOnFocus, cn } from '@/lib/utils'

const POSITION_ROWS: { elementType: number; label: string }[] = [
  { elementType: 1, label: 'Goalkeeper' },
  { elementType: 2, label: 'Defenders' },
  { elementType: 3, label: 'Midfielders' },
  { elementType: 4, label: 'Attackers' },
]

type ViewMode = 'lineup' | 'table'
type FormMode = 'current' | 'season'

const MVP_STAT_OPTIONS: { value: FplMvpStat; label: string }[] = [
  { value: 'points', label: 'Points' },
  { value: 'points_per_game', label: 'Points Per Game' },
  { value: 'highest_gw', label: 'Highest Points in a GW' },
  { value: 'cost_per_point', label: 'Cost Per Point' },
]

function formatMvpValue(stat: FplMvpStat, mvp: FplEntryMvp): string {
  if (mvp.points == null) return '—'
  if (stat === 'points_per_game') return mvp.points.toFixed(1)
  if (stat === 'cost_per_point') return `£${mvp.points.toFixed(2)}m`
  return mvp.points.toLocaleString()
}

function pickPoints(pick: FplPick): number | null {
  return pick.player.points != null ? pick.player.points * pick.multiplier : null
}

function ordinal(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

// Simple relative-luminance check so the team-color pill behind the team
// name always keeps its text readable, whichever club's color lands there.
function contrastTextColor(hex: string): string {
  const clean = hex.replace('#', '')
  if (clean.length !== 6) return '#ffffff'
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? '#000000' : '#ffffff'
}

function PickRow({ pick, index, maxPoints }: { pick: FplPick; index: number; maxPoints: number }) {
  const points = pickPoints(pick)
  const barWidth = Math.max(4, (Math.abs(points ?? 0) / maxPoints) * 60)
  return (
    <div
      style={{ animationDelay: `${index * 25}ms`, animationFillMode: 'backwards' }}
      className="flex animate-in items-center gap-2 border-b border-white/10 px-2 py-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out last:border-b-0 sm:gap-3"
    >
      <span className="flex size-5 shrink-0 items-center justify-center">
        {pick.player.team_logo_url && (
          <img src={pick.player.team_logo_url} alt="" className="size-5 object-contain" />
        )}
      </span>
      <span className="flex size-4 shrink-0 items-center justify-center">
        {pick.player.country_flag_3d_url && (
          <img src={pick.player.country_flag_3d_url} alt="" className="size-4 object-contain" />
        )}
      </span>
      <span className="w-24 shrink-0 truncate text-sm text-white sm:w-32">
        {pick.player.web_name}
        {pick.is_captain && <span className="ml-1 text-xs font-bold text-yellow-400">C</span>}
        {pick.is_vice_captain && <span className="ml-1 text-xs text-white/40">VC</span>}
      </span>
      <span className="relative flex min-w-0 flex-1 items-center sm:gap-1.5">
        <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10 sm:h-1.5">
          <span
            className="block h-full rounded-full transition-[width] duration-500 ease-out"
            style={{ width: `${barWidth}%`, backgroundColor: pick.player.team_color_hex || '#dc2626' }}
          />
        </span>
        <span
          className={cn(
            'absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-sm font-semibold sm:static sm:top-auto sm:shrink-0 sm:translate-y-0 sm:pl-0 sm:text-right sm:text-base',
            pick.is_captain ? 'text-yellow-400' : 'text-white'
          )}
          style={{ left: `${barWidth}%` }}
        >
          {points ?? '-'}
        </span>
      </span>
    </div>
  )
}

function PointsBarChart({
  bars,
  colorHex,
}: {
  bars: { label: string; value: number | null }[]
  colorHex: string
}) {
  if (bars.length === 0) {
    return <p className="py-8 text-center text-sm text-white/40">No data available.</p>
  }
  const maxValue = Math.max(1, ...bars.map((b) => Math.abs(b.value ?? 0)))
  return (
    <div className="flex items-end justify-center gap-2 overflow-x-auto px-2 pb-1 pt-6 sm:gap-3">
      {bars.map((b, i) => {
        const heightPct = Math.max((Math.abs(b.value ?? 0) / maxValue) * 100, 2)
        return (
          <div
            key={b.label}
            style={{ animationDelay: `${i * 40}ms`, animationFillMode: 'backwards' }}
            className="flex w-10 shrink-0 animate-in flex-col items-center gap-2 fade-in-0 slide-in-from-bottom-3 duration-300 ease-out sm:w-12"
          >
            <div className="flex h-32 w-full flex-col items-center justify-end">
              <span className="mb-1 text-xs font-semibold text-white">{b.value ?? '—'}</span>
              <div
                style={{ height: `${heightPct}%`, backgroundColor: `${colorHex}30`, borderColor: colorHex }}
                className="w-7 rounded-none border transition-all duration-300 ease-out sm:w-9"
              />
            </div>
            <span className="text-[10px] text-white/50">{b.label}</span>
          </div>
        )
      })}
    </div>
  )
}

function BarChartSkeleton() {
  return (
    <div className="flex items-end justify-center gap-2 px-2 pb-1 pt-6 sm:gap-3">
      {[60, 90, 40, 75, 55, 30].map((h, i) => (
        <div key={i} className="flex w-10 shrink-0 flex-col items-center gap-2 sm:w-12">
          <div className="flex h-32 w-full flex-col items-center justify-end">
            <div
              style={{ height: `${h}%`, animationDelay: `${i * 80}ms` }}
              className="w-7 animate-pulse rounded-none bg-white/10 sm:w-9"
            />
          </div>
          <div className="h-2.5 w-6 animate-pulse rounded bg-white/10" />
        </div>
      ))}
    </div>
  )
}

function RowsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="mt-3 flex flex-col gap-2">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} style={{ animationDelay: `${i * 60}ms` }} className="flex animate-pulse items-center gap-3 px-2 py-1">
          <div className="size-5 shrink-0 rounded-full bg-white/10" />
          <div className="h-3.5 w-28 rounded bg-white/10" />
          <div className="h-2 min-w-0 flex-1 rounded-full bg-white/10" />
        </div>
      ))}
    </div>
  )
}

function NationalityRows({ data }: { data: FplEntryNationality[] }) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const maxCount = Math.max(1, ...data.map((d) => d.count))

  return (
    <div>
      {data.map((d, i) => {
        const barWidth = Math.max(4, (d.count / maxCount) * 60)
        const isOpen = expanded === d.country_name
        return (
          <div key={d.country_name} className="border-b border-white/10 last:border-b-0">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpanded((cur) => (cur === d.country_name ? null : d.country_name))}
              style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
              className="flex animate-in cursor-pointer items-center gap-2 px-2 py-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out hover:bg-white/5 sm:gap-3"
            >
              <span className="flex size-5 shrink-0 items-center justify-center">
                {d.circle_url ? (
                  <img src={d.circle_url} alt="" className="size-5 rounded-full object-cover" />
                ) : (
                  <div className="size-5 rounded-full bg-white/10" />
                )}
              </span>
              <span className="w-24 shrink-0 truncate text-sm text-white sm:w-32">{d.country_name}</span>
              <span className="relative flex min-w-0 flex-1 items-center sm:gap-1.5">
                <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10 sm:h-1.5">
                  <span
                    className="block h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{ width: `${barWidth}%`, backgroundColor: d.hex_color || '#ffffff' }}
                  />
                </span>
                <span
                  className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-sm font-semibold text-white sm:static sm:top-auto sm:shrink-0 sm:translate-y-0 sm:pl-0 sm:text-right sm:text-base"
                  style={{ left: `${barWidth}%` }}
                >
                  {d.count}
                </span>
              </span>
            </div>
            {isOpen && (
              <div className="flex flex-col gap-1 py-1 animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out">
                {d.players.map((p) => (
                  <div key={p.name} className="flex items-center gap-2 py-1 pl-8 text-sm">
                    <span className="flex size-4 shrink-0 items-center justify-center">
                      {p.team_logo_url && <img src={p.team_logo_url} alt="" className="size-4 object-contain" />}
                    </span>
                    <span className="truncate text-white/80">{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export function FplAnalyzePage() {
  const { entryId: entryIdParam } = useParams<{ entryId?: string }>()
  const router = useRouter()

  const [entryIdInput, setEntryIdInput] = useState(entryIdParam ?? '')
  const [nameQuery, setNameQuery] = useState('')
  const [entry, setEntry] = useState<FplEntrySummary | null>(null)
  const [loadingEntry, setLoadingEntry] = useState(false)
  const [entryError, setEntryError] = useState<string | null>(null)

  const { data: nameResults, isLoading: nameLoading, isFetching: nameFetching } = useFplEntrySearch(nameQuery)
  const nameMatches = nameResults?.data ?? []

  const [gameweek, setGameweek] = useState<number | null>(null)
  const [view, setView] = useState<ViewMode>('table')
  const [formMode, setFormMode] = useState<FormMode>('current')
  const [mvpStat, setMvpStat] = useState<FplMvpStat>('points')
  const [mvpMenuOpen, setMvpMenuOpen] = useState(false)
  const [leaguesGameweek, setLeaguesGameweek] = useState<FplLeagueGameweekSelection>('season')
  const [leaguesGwMenuOpen, setLeaguesGwMenuOpen] = useState(false)
  const [marketValueExpanded, setMarketValueExpanded] = useState(false)

  const { data: currentGwData } = useFplCurrentGameweek()
  const currentGameweek = currentGwData?.data.gameweek_number ?? null
  const minGameweek = currentGameweek != null ? Math.max(1, currentGameweek - 2) : null

  async function handleSearch(id: number) {
    setLoadingEntry(true)
    setEntryError(null)
    try {
      const res = await syncFplEntry(id)
      setEntry(res.data)
      setGameweek(null)
      if (entryIdParam !== String(id)) router.push(`/fpl/users/${id}`)
    } catch (err) {
      setEntry(null)
      setEntryError(err instanceof FplApiError ? err.message : 'Failed to load that FPL team.')
    } finally {
      setLoadingEntry(false)
    }
  }

  // Auto-search when the URL already names an entry id (e.g. shared link).
  useEffect(() => {
    if (entryIdParam && !entry && !loadingEntry) {
      const id = Number(entryIdParam)
      if (Number.isFinite(id)) {
        setEntryIdInput(entryIdParam)
        handleSearch(id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryIdParam])

  useEffect(() => {
    if (currentGameweek != null && gameweek == null) setGameweek(currentGameweek)
  }, [currentGameweek, gameweek])

  const { data: picksData, isLoading: picksLoading } = useFplEntryPicks(entry?.id ?? null, gameweek)
  const picks = picksData?.data.picks ?? []
  const lineupPicks = picks.filter((p) => p.position <= 11)
  const benchPicks = picks.filter((p) => p.position >= 12).sort((a, b) => a.position - b.position)

  const { data: gwHistoryData, isLoading: gwHistoryLoading } = useFplEntryGwHistory(entry?.id ?? null)
  const { data: seasonHistoryData, isLoading: seasonHistoryLoading } = useFplEntrySeasonHistory(entry?.id ?? null)
  const { data: mvpsData, isLoading: mvpsLoading } = useFplEntryMvps(entry?.id ?? null, mvpStat, 4)
  const { data: nationalitiesData, isLoading: nationalitiesLoading } = useFplEntryNationalities(
    entry?.id ?? null,
    gameweek
  )
  const { data: leaguesData, isLoading: leaguesLoading } = useFplEntryLeagues(entry?.id ?? null, leaguesGameweek)
  const { data: marketValueData, isLoading: marketValueLoading } = useFplEntryMarketValue(
    entry?.id ?? null,
    gameweek
  )
  const formLoading = formMode === 'current' ? gwHistoryLoading : seasonHistoryLoading

  const mvps = mvpsData?.data ?? []
  const nationalities = nationalitiesData?.data ?? []
  const leagues = leaguesData?.data ?? []
  const marketValuePlayers = marketValueData?.data ?? []
  const mvpMax = Math.max(1, ...mvps.map((m) => Math.abs(m.points ?? 0)))

  const formBars = useMemo(() => {
    if (formMode === 'season') {
      return (seasonHistoryData?.data ?? []).map((s) => ({ label: s.season_name, value: s.total_points }))
    }
    return (gwHistoryData?.data ?? []).map((g) => ({ label: `GW${g.event}`, value: g.points }))
  }, [formMode, gwHistoryData, seasonHistoryData])

  const leaguesGwOptions = useMemo(() => {
    if (currentGameweek == null) return ['season' as const]
    const nums = Array.from({ length: currentGameweek }, (_, i) => i + 1)
    return [...nums, 'season' as const]
  }, [currentGameweek])

  const atMin = gameweek != null && minGameweek != null && gameweek <= minGameweek
  const atMax = gameweek != null && currentGameweek != null && gameweek >= currentGameweek

  const fullName = useMemo(() => {
    if (!entry) return ''
    return [entry.player_first_name, entry.player_last_name].filter(Boolean).join(' ')
  }, [entry])

  function submitSearch(e: React.FormEvent) {
    e.preventDefault()
    const id = Number(entryIdInput.trim())
    if (Number.isFinite(id) && id > 0) handleSearch(id)
  }

  function handleBack() {
    setEntry(null)
    setEntryIdInput('')
    setNameQuery('')
    setEntryError(null)
    router.push('/fpl/users')
  }

  const teamColor = entry?.favourite_team_color_hex || null
  const barColor = teamColor || '#ffffff'

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-4 py-6 text-center">
      {entry && !loadingEntry && (
        <button
          type="button"
          onClick={handleBack}
          className="flex w-full items-center gap-1.5 text-left text-sm text-white/60 transition-colors hover:text-white"
        >
          <ArrowLeft className="size-4" />
          Back
        </button>
      )}

      {!entry && (
        <>
          <div>
            <h2 className="text-xl font-semibold text-white">Get Insights on your FPL team</h2>
          </div>

          <div className="w-full max-w-xs">
            <input
              type="text"
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              onFocus={pinScrollOnFocus}
              placeholder="Search by FPL team name…"
              className="w-full rounded-md border border-white/10 bg-black px-3 py-2 text-center text-sm text-white outline-none placeholder:text-white/30 focus:border-white/30"
            />
          </div>

          {nameQuery.trim().length >= 2 && (nameLoading || nameFetching) && (
            <div className="flex justify-center py-2">
              <Spinner className="size-5 text-white/50" />
            </div>
          )}

          {nameQuery.trim().length >= 2 && !nameLoading && nameMatches.length === 0 && (
            <p className="text-sm text-white/40">No teams found — try your FPL ID below.</p>
          )}

          {nameMatches.length > 0 && (
            <div className="w-full max-w-xs rounded-xl border border-white/10 bg-[#0e1114] text-left">
              {nameMatches.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => handleSearch(m.id)}
                  style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
                  className="flex w-full animate-in flex-col border-b border-white/10 px-3 py-2 text-left fade-in-0 slide-in-from-bottom-1 transition-colors duration-200 ease-out last:border-b-0 hover:bg-white/5"
                >
                  <span className="truncate text-sm text-white">{m.team_name}</span>
                  {m.player_name && <span className="truncate text-xs text-white/50">{m.player_name}</span>}
                </button>
              ))}
            </div>
          )}

          <div className="flex w-full max-w-xs items-center gap-2 text-xs text-white/30">
            <span className="h-px flex-1 bg-white/10" />
            can&apos;t find your team? enter your FPL ID
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submitSearch} className="flex w-full max-w-xs flex-col items-center gap-3">
            <input
              type="text"
              inputMode="numeric"
              value={entryIdInput}
              onChange={(e) => setEntryIdInput(e.target.value)}
              onFocus={pinScrollOnFocus}
              placeholder="Enter your FPL entry ID…"
              className="w-full rounded-md border border-white/10 bg-black px-3 py-2 text-center text-sm text-white outline-none placeholder:text-white/30 focus:border-white/30"
            />
            <button
              type="submit"
              disabled={loadingEntry || !entryIdInput.trim()}
              className="rounded-md bg-white px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:opacity-50"
            >
              Search team
            </button>
          </form>
        </>
      )}

      {loadingEntry && (
        <div className="flex flex-col items-center gap-2 py-6">
          <Spinner className="size-6 text-white/50" />
          <p className="text-sm text-white/40">Fetching FPL data…</p>
        </div>
      )}

      {entryError && <p className="text-sm text-red-400">{entryError}</p>}

      {entry && !loadingEntry && (
        <div key={entry.id} className="w-full animate-in text-left fade-in-0 slide-in-from-bottom-2 duration-300 ease-out">
          <div className="flex flex-col items-start gap-3 rounded-xl border border-white/10 bg-[#0e1114] p-4 text-left">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold text-white">{entry.team_name}</span>
              {entry.favourite_team_logo_url && (
                <img src={entry.favourite_team_logo_url} alt="" className="size-9 shrink-0 object-contain" />
              )}
            </div>

            <p
              className="rounded-lg px-3 py-1.5 text-sm font-medium"
              style={{
                backgroundColor: teamColor || '#27272a',
                color: teamColor ? contrastTextColor(teamColor) : '#ffffff',
              }}
            >
              {fullName}
            </p>

            {entry.player_region_name && (
              <p className="flex items-center gap-1.5 text-sm text-white/60">
                Region: {entry.player_region_name}
                {entry.country_flag_url && (
                  <img src={entry.country_flag_url} alt="" className="size-4 rounded-full object-cover" />
                )}
              </p>
            )}

            <div className="flex flex-col gap-1 text-base text-white/70">
              <p>
                Total Points:{' '}
                <span className="text-lg font-semibold text-white">{entry.summary_overall_points ?? '—'}</span>
                {entry.summary_overall_rank != null && (
                  <span>
                    {' ('}
                    <span style={{ color: teamColor || undefined }} className={teamColor ? 'font-semibold' : ''}>
                      {ordinal(entry.summary_overall_rank)}
                    </span>
                    <span className="text-white/50"> in the world)</span>
                  </span>
                )}
              </p>
              <p>
                GW{entry.current_event} Points:{' '}
                <span className="text-lg font-semibold text-white">{entry.summary_event_points ?? '—'}</span>
                {entry.summary_event_rank != null && (
                  <span>
                    {' ('}
                    <span style={{ color: teamColor || undefined }} className={teamColor ? 'font-semibold' : ''}>
                      {ordinal(entry.summary_event_rank)}
                    </span>
                    <span className="text-white/50"> in the world)</span>
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="mt-6 animate-in rounded-xl border border-white/10 bg-[#0a0c0e] p-4 fade-in-0 slide-in-from-bottom-2 duration-300 ease-out">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-white">Form</h3>
              <div className="flex items-center rounded-md border border-white/10 p-0.5">
                {(['current', 'season'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setFormMode(m)}
                    className={cn(
                      'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                      formMode === m ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                    )}
                  >
                    {m === 'current' ? 'Current' : 'All Season'}
                  </button>
                ))}
              </div>
            </div>
            {formLoading ? <BarChartSkeleton /> : <PointsBarChart key={formMode} bars={formBars} colorHex={barColor} />}
          </div>

          <div className="mt-6 animate-in rounded-xl border border-white/10 bg-[#0a0c0e] p-4 fade-in-0 slide-in-from-bottom-2 duration-300 ease-out">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-white">{entry.team_name}&apos;s MVPs</h3>
              <DropdownMenu open={mvpMenuOpen} onOpenChange={setMvpMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-md border border-white/10 px-2.5 py-1.5 text-sm text-white/80"
                  >
                    {MVP_STAT_OPTIONS.find((o) => o.value === mvpStat)?.label}
                    <ChevronDown className="size-3.5 text-white/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {MVP_STAT_OPTIONS.map((o) => (
                    <DropdownMenuItem
                      key={o.value}
                      onClick={() => setMvpStat(o.value)}
                      className={cn(mvpStat === o.value ? 'font-medium text-white' : 'text-foreground')}
                    >
                      {o.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {mvpsLoading ? (
              <RowsSkeleton />
            ) : mvps.length === 0 ? (
              <p className="py-4 text-center text-sm text-white/40">No data yet.</p>
            ) : (
              <div key={mvpStat} className="mt-3">
                {mvps.map((mvp, i) => {
                  const barWidth = Math.max(4, (Math.abs(mvp.points ?? 0) / mvpMax) * 60)
                  return (
                    <div
                      key={mvp.fpl_id}
                      style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
                      className="flex animate-in items-center gap-2 border-b border-white/10 px-2 py-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out last:border-b-0 sm:gap-3"
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center">
                        {mvp.team_logo_url ? (
                          <img src={mvp.team_logo_url} alt="" className="size-5 object-contain" />
                        ) : (
                          <div className="size-5 rounded-full bg-white/10" />
                        )}
                      </span>
                      <Link
                        href={`/fpl/players/${mvp.fpl_id}`}
                        className="fpl-row-link w-32 shrink-0 truncate text-sm sm:w-40"
                      >
                        {mvp.name}
                      </Link>
                      <span className="relative flex min-w-0 flex-1 items-center sm:gap-1.5">
                        <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-white/10 sm:h-1.5">
                          <span
                            className="block h-full rounded-full transition-[width] duration-500 ease-out"
                            style={{ width: `${barWidth}%`, backgroundColor: mvp.team_color_hex || '#ffffff' }}
                          />
                        </span>
                        <span
                          className="absolute top-1/2 -translate-y-1/2 whitespace-nowrap pl-1.5 text-sm font-semibold text-white sm:static sm:top-auto sm:shrink-0 sm:translate-y-0 sm:pl-0 sm:text-right sm:text-base"
                          style={{ left: `${barWidth}%` }}
                        >
                          {formatMvpValue(mvpStat, mvp)}
                          {mvpStat === 'highest_gw' && mvp.gw != null && (
                            <span className="ml-1 text-xs font-normal text-white/50">GW{mvp.gw}</span>
                          )}
                        </span>
                      </span>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous gameweek"
                disabled={gameweek == null || atMin}
                onClick={() => setGameweek((gw) => (gw != null && minGameweek != null ? Math.max(minGameweek, gw - 1) : gw))}
                className="flex size-7 items-center justify-center rounded-md border border-white/10 text-white/60 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronLeft className="size-4" />
              </button>
              <span className="min-w-16 text-center text-sm text-white/70">
                {gameweek != null ? `GW ${gameweek}` : '—'}
              </span>
              <button
                type="button"
                aria-label="Next gameweek"
                disabled={gameweek == null || atMax}
                onClick={() =>
                  setGameweek((gw) => (gw != null && currentGameweek != null ? Math.min(currentGameweek, gw + 1) : gw))
                }
                className="flex size-7 items-center justify-center rounded-md border border-white/10 text-white/60 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>

            <div className="flex items-center rounded-md border border-white/10 p-0.5">
              {(['lineup', 'table'] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => setView(v)}
                  className={cn(
                    'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                    view === v ? 'bg-white text-black' : 'text-white/60 hover:text-white'
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {picksLoading && (
            <div className="flex justify-center py-8">
              <Spinner className="size-5 text-white/50" />
            </div>
          )}

          {!picksLoading && picksData && (
            <div
              key={`${gameweek}-${view}`}
              className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out"
            >
              <div className="mt-3 flex flex-col items-center">
                <p className="text-3xl font-extrabold text-white">{picksData.data.points ?? '—'} points</p>
                <p className="text-xs text-white/50">{picksData.data.points_on_bench ?? '—'} on bench</p>
              </div>

              {view === 'lineup' ? (
                <div className="mt-4 flex flex-col gap-5">
                  {POSITION_ROWS.map((row, rowIndex) => {
                    const rowPicks = lineupPicks.filter((p) => p.element_type === row.elementType)
                    if (rowPicks.length === 0) return null
                    return (
                      <div
                        key={row.elementType}
                        style={{ animationDelay: `${rowIndex * 60}ms`, animationFillMode: 'backwards' }}
                        className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out"
                      >
                        <div className="flex flex-wrap justify-center gap-2">
                          {rowPicks.map((p) => (
                            <div key={p.position} className="w-16 sm:w-20">
                              <FplPlayerCard
                                webName={p.player.web_name}
                                teamLogoUrl={p.player.team_logo_url}
                                teamColorHex={p.player.team_color_hex}
                                countryFlagUrl={p.player.country_flag_3d_url}
                                points={pickPoints(p)}
                                isCaptain={p.is_captain}
                                isViceCaptain={p.is_vice_captain}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                  {benchPicks.length > 0 && (
                    <div
                      style={{ animationDelay: `${POSITION_ROWS.length * 60}ms`, animationFillMode: 'backwards' }}
                      className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300 ease-out"
                    >
                      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-white/35">Bench</p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {benchPicks.map((p) => (
                          <div key={p.position} className="w-16 sm:w-20">
                            <FplPlayerCard
                              webName={p.player.web_name}
                              teamLogoUrl={p.player.team_logo_url}
                              teamColorHex={p.player.team_color_hex}
                              countryFlagUrl={p.player.country_flag_3d_url}
                              points={pickPoints(p)}
                              isCaptain={p.is_captain}
                              isViceCaptain={p.is_vice_captain}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-white/10 bg-[#0e1114]">
                  {(() => {
                    const maxPoints = Math.max(1, ...picks.map((p) => Math.abs(pickPoints(p) ?? 0)))
                    let globalIndex = 0
                    return POSITION_ROWS.map((row) => {
                      const rowPicks = lineupPicks.filter((p) => p.element_type === row.elementType)
                      if (rowPicks.length === 0) return null
                      return (
                        <div key={row.elementType} className="border-b-4 border-white/20">
                          {rowPicks.map((p) => (
                            <PickRow key={p.position} pick={p} index={globalIndex++} maxPoints={maxPoints} />
                          ))}
                        </div>
                      )
                    }).concat(
                      benchPicks.length > 0 ? (
                        <div key="bench">
                          {benchPicks.map((p) => (
                            <PickRow key={p.position} pick={p} index={globalIndex++} maxPoints={maxPoints} />
                          ))}
                        </div>
                      ) : null
                    )
                  })()}
                </div>
              )}
            </div>
          )}

          <div className="mt-6 animate-in rounded-xl border border-white/10 bg-[#0a0c0e] p-4 fade-in-0 slide-in-from-bottom-2 duration-300 ease-out">
            <h3 className="text-base font-semibold text-white">Squad Nationalities</h3>
            {nationalitiesLoading ? (
              <RowsSkeleton />
            ) : nationalities.length === 0 ? (
              <p className="py-4 text-center text-sm text-white/40">No squad data yet.</p>
            ) : (
              <div className="mt-3">
                <NationalityRows data={nationalities} />
              </div>
            )}
          </div>

          <div className="mt-6 animate-in rounded-xl border border-white/10 bg-[#0a0c0e] p-4 fade-in-0 slide-in-from-bottom-2 duration-300 ease-out">
            <h3 className="text-base font-semibold text-white">Real Life Market Value</h3>
            {marketValueLoading ? (
              <RowsSkeleton />
            ) : marketValuePlayers.length === 0 ? (
              <p className="py-4 text-center text-sm text-white/40">No squad data yet.</p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setMarketValueExpanded((v) => !v)}
                  className="mt-1 flex items-center gap-2 text-2xl font-bold text-white"
                >
                  {marketValueData?.total_market_value != null
                    ? `€${(marketValueData.total_market_value / 1_000_000).toFixed(1)}m`
                    : '—'}
                  {marketValueData?.rank != null && (
                    <span className="text-sm font-medium text-white/50">({ordinal(marketValueData.rank)})</span>
                  )}
                  <ChevronDown
                    className={cn('size-4 text-white/50 transition-transform', marketValueExpanded && 'rotate-180')}
                  />
                </button>
                {marketValueExpanded && (
                  <div className="mt-3 animate-in fade-in-0 slide-in-from-top-1 duration-200 ease-out">
                    {marketValuePlayers.map((p, i) => (
                      <div
                        key={p.fpl_id}
                        style={{ animationDelay: `${i * 25}ms`, animationFillMode: 'backwards' }}
                        className="flex animate-in items-center gap-2 border-b border-white/10 px-2 py-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out last:border-b-0 sm:gap-3"
                      >
                        <span className="flex size-5 shrink-0 items-center justify-center">
                          {p.team_logo_url ? (
                            <img src={p.team_logo_url} alt="" className="size-5 object-contain" />
                          ) : (
                            <div className="size-5 rounded-full bg-white/10" />
                          )}
                        </span>
                        <span className="flex size-4 shrink-0 items-center justify-center">
                          {p.country_flag_url && (
                            <img src={p.country_flag_url} alt="" className="size-4 rounded-full object-cover" />
                          )}
                        </span>
                        <Link href={`/fpl/players/${p.fpl_id}`} className="fpl-row-link min-w-0 flex-1 truncate text-sm">
                          {p.name}
                        </Link>
                        <span className="w-8 shrink-0 text-right text-xs text-white/50">{p.age ?? '—'}</span>
                        <span className="w-9 shrink-0 text-right text-xs text-white/50">{p.position ?? '—'}</span>
                        <span className="w-16 shrink-0 text-right text-sm font-semibold text-white">
                          {p.market_value != null ? `€${(p.market_value / 1_000_000).toFixed(1)}m` : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="mt-6 animate-in rounded-xl border border-white/10 bg-[#0a0c0e] p-4 fade-in-0 slide-in-from-bottom-2 duration-300 ease-out">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-base font-semibold text-white">Leagues</h3>
              <DropdownMenu open={leaguesGwMenuOpen} onOpenChange={setLeaguesGwMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded-md border border-white/10 px-2.5 py-1.5 text-sm text-white/80"
                  >
                    {leaguesGameweek === 'season' ? 'Season' : `GW ${leaguesGameweek}`}
                    <ChevronDown className="size-3.5 text-white/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-64 w-32 overflow-y-auto">
                  {leaguesGwOptions.map((opt) => (
                    <DropdownMenuItem
                      key={opt}
                      onClick={() => setLeaguesGameweek(opt)}
                      className={cn(leaguesGameweek === opt ? 'font-medium text-white' : 'text-foreground')}
                    >
                      {opt === 'season' ? 'Season' : `GW ${opt}`}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="mt-3 flex items-center gap-2 px-2 pb-1 text-[10px] uppercase tracking-wider text-white/35 sm:gap-3">
              <span className="min-w-0 flex-1">League name</span>
              <span className="w-14 shrink-0 text-right"># of users</span>
              <span className="w-14 shrink-0 text-right">Rank</span>
            </div>

            {leaguesLoading ? (
              <RowsSkeleton />
            ) : leagues.length === 0 ? (
              <p className="py-4 text-center text-sm text-white/40">Not in any leagues yet.</p>
            ) : (
              <div key={leaguesGameweek}>
                {leagues.map((lg, i) => (
                  <div
                    key={lg.league_id}
                    style={{ animationDelay: `${i * 30}ms`, animationFillMode: 'backwards' }}
                    className="flex animate-in items-center gap-2 border-b border-white/10 px-2 py-2 fade-in-0 slide-in-from-left-1 duration-300 ease-out last:border-b-0 sm:gap-3"
                  >
                    <Link href={`/fpl/leagues/${lg.league_id}`} className="fpl-row-link min-w-0 flex-1 truncate text-sm">
                      {lg.league_name}
                    </Link>
                    <span className="w-14 shrink-0 text-right text-sm text-white/70">
                      {lg.member_count.toLocaleString()}
                    </span>
                    <span className="w-14 shrink-0 text-right text-sm font-semibold text-white">
                      {lg.rank != null ? lg.rank.toLocaleString() : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
